// BL-240 Gate 2 — staff-initiated reply helpers (frontend only).
//
// Gate 1 (the `staff-send-reply` Edge Function, verify_jwt: true) is already live and is NOT
// touched here. This module holds the pure, testable half of the composer: which address a
// session replies to, what a channel will actually carry, what the request body looks like,
// and how a just-sent message is rendered optimistically until the next poll returns the
// server's own log entry.
//
// Same shape as src/lib/twilioIntake.js / src/lib/emailIntake.js: no Supabase or provider SDK
// calls in the pure helpers, so they unit test as plain functions. Only sendStaffReply()
// touches the network, and it does so through supabase-js functions.invoke() so the signed-in
// staff member's JWT is attached automatically (the function's verify_jwt gate).

import { extraEmailRecipients, sanitizeEmailHtml } from './emailHtml';
import { newClientRef } from './optimisticMessages';
import { supabaseClient } from '../config/supabase';

export const STAFF_REPLY_FUNCTION = 'staff-send-reply';

export const STAFF_REPLY_CHANNELS = ['sms', 'mms', 'whatsapp', 'email'];
export const STAFF_REPLY_PICKER_CHANNELS = ['sms', 'whatsapp', 'email'];

// Per-channel body ceilings the composer enforces before spending a round trip. SMS/MMS mirrors
// Twilio's 1600-character limit for a single Messages request; WhatsApp's freeform body limit is
// 4096. Email has no comparable hard limit — the value below is a sanity ceiling, not a protocol
// rule, so a runaway paste is caught client-side instead of at the provider.
export const CHANNEL_BODY_LIMITS = {
    sms: 1600,
    mms: 1600,
    whatsapp: 4096,
    email: 25000,
};

const CHANNEL_LABELS = {
    sms: 'SMS',
    mms: 'MMS',
    whatsapp: 'WhatsApp',
    email: 'Email',
};

export function describeChannel(channel) {
    return CHANNEL_LABELS[channel] || 'this channel';
}

export function bodyLimitForChannel(channel) {
    return CHANNEL_BODY_LIMITS[channel] ?? CHANNEL_BODY_LIMITS.sms;
}

export function isEmailChannel(channel) {
    return channel === 'email';
}

/**
 * Resolves who a reply goes to from the intake session row itself. Email sessions reply to
 * contact_email; every Twilio channel replies to contact_phone. The Edge Function resolves the
 * recipient server-side too — this is the client-side check that stops the composer offering a
 * send it already knows has nowhere to go.
 */
function resolveReplyChannel(session, channel = null) {
    return channel || session?.channel || null;
}

export function resolveReplyRecipient(session, channel = null) {
    if (!session) return { channel: null, address: '' };
    const replyChannel = resolveReplyChannel(session, channel);
    const address = isEmailChannel(replyChannel)
        ? (session.contact_email || '')
        : (session.contact_phone || '');
    return { channel: replyChannel, address: typeof address === 'string' ? address.trim() : '' };
}

/**
 * Client-side validation. Returns { valid, error } — never throws, so the composer can render
 * the reason inline rather than in a catch block.
 */
export function validateStaffReply({ session, body = '', channel: selectedChannel = null, contentSid = '', mediaUrl = null } = {}) {
    const { channel, address } = resolveReplyRecipient(session, selectedChannel);
    if (!session?.id) {
        return { valid: false, error: 'No intake session is selected.' };
    }
    if (!channel || !STAFF_REPLY_CHANNELS.includes(channel)) {
        return { valid: false, error: `Replies are not supported on the "${session.channel || 'unknown'}" channel yet.` };
    }
    if (!address) {
        return {
            valid: false,
            error: isEmailChannel(channel)
                ? 'This session has no contact email to reply to.'
                : 'This session has no contact phone number to reply to.',
        };
    }
    const trimmedBody = typeof body === 'string' ? body.trim() : '';
    if (!trimmedBody && !(typeof contentSid === 'string' && contentSid.trim()) && !mediaUrl) {
        return { valid: false, error: 'Type a message before sending.' };
    }
    const limit = bodyLimitForChannel(channel);
    if (trimmedBody.length > limit) {
        return { valid: false, error: `${describeChannel(channel)} messages are limited to ${limit} characters.` };
    }
    return { valid: true, error: null };
}

/**
 * Builds the `staff-send-reply` request body. The sessionId stays fixed to the open thread while
 * the selected channel tells the function which delivery path to use for this one outbound reply.
 * The function still owns recipient resolution from the intake_sessions row; the client never
 * sends a phone number or email address.
 */
export function buildStaffReplyPayload({
    session,
    body = '',
    channel: selectedChannel = null,
    subject = '',
    mediaUrl = null,
    contentSid = '',
    contentVariables = null,
    templateBody = '',
    sendingIdentityId = null,
    sendingIdentityExplicitlyChosen = false,
    campaignRecipientId = null,
    orgId = '',
    recipientAddress = '',
    groupAddresses = null,
    retryClientRef = '',
    clientRef = '',
    cc = '',
    bcc = '',
    html = '',
} = {}) {
    const channel = resolveReplyChannel(session, selectedChannel);
    const payload = {
        sessionId: session?.id ?? null,
        body: typeof body === 'string' ? body.trim() : '',
    };
    if (channel) payload.channel = channel;
    if (isEmailChannel(channel)) {
        payload.subject = typeof subject === 'string' ? subject.trim() : '';
        // 1.12.212-beta: Cc / Bcc (the server checks each one and the Comms TEST door) and the formatted body.
        const copies = extraEmailRecipients({ to: session?.contact_email || recipientAddress || '', cc, bcc });
        if (copies.cc.length) payload.cc = copies.cc;
        if (copies.bcc.length) payload.bcc = copies.bcc;
        const safeHtml = sanitizeEmailHtml(html);
        if (safeHtml) payload.html = safeHtml;
    }
    if (mediaUrl) payload.mediaUrl = mediaUrl;
    if (typeof contentSid === 'string' && contentSid.trim()) payload.contentSid = contentSid.trim();
    if (contentVariables && typeof contentVariables === 'object') payload.contentVariables = contentVariables;
    if (typeof templateBody === 'string' && templateBody) payload.templateBody = templateBody;
    if (sendingIdentityId) payload.sendingIdentityId = sendingIdentityId;
    if (sendingIdentityExplicitlyChosen === true) payload.sendingIdentityExplicitlyChosen = true;
    if (campaignRecipientId) payload.campaignRecipientId = campaignRecipientId;
    if (orgId) payload.orgId = orgId;
    if (recipientAddress) payload.recipientAddress = recipientAddress;
    // BL-177 Step 2e (6b): a NEW group text names everyone on it; the server builds the thread key and sends once.
    if (Array.isArray(groupAddresses) && groupAddresses.length) payload.groupAddresses = groupAddresses;
    if (retryClientRef) payload.retryClientRef = retryClientRef;
    // BL-177 Step 2d (live bug): the browser picks the send's client_ref so the optimistic bubble and the saved row
    // share it and the thread shows the text once.
    if (typeof clientRef === 'string' && clientRef.trim()) payload.clientRef = clientRef.trim();
    return payload;
}

/**
 * Optimistic conversation entry for a message this staff member just sent, shaped like the
 * outbound entries the backend appends so the panel reads consistently until the next poll
 * replaces it with the server's own row.
 */
export function buildOptimisticOutboundEntry({
    body = '',
    channel = null,
    sentAt = null,
    staffUserId = null,
    mediaUrl = null,
    contentSid = '',
    contentVariables = null,
    clientRef = '',
} = {}) {
    return {
        direction: 'outbound',
        author: 'staff',
        channel,
        body: typeof body === 'string' ? body.trim() : '',
        sent_at: sentAt || new Date().toISOString(),
        pending: true,
        ...(staffUserId ? { staffUserId } : {}),
        ...(mediaUrl ? { media_url: mediaUrl } : {}),
        ...(contentSid ? { contentSid } : {}),
        ...(contentVariables ? { contentVariables } : {}),
        ...(clientRef ? { client_ref: clientRef } : {}),
    };
}

/**
 * Reads the function's own `error` string out of a non-2xx response. supabase-js raises a
 * FunctionsHttpError carrying the raw Response on `.context`, and reports only a generic
 * "non-2xx status code" message of its own — so the 401/403/404/422/502 explanations the
 * function actually writes are only reachable by reading that body.
 */
export async function readFunctionError(error) {
    const response = error?.context;
    if (response && typeof response.json === 'function') {
        try {
            const parsed = await response.json();
            if (typeof parsed?.error === 'string' && parsed.error) {
                return { message: parsed.error, outcome: parsed.outcome || null, sessionId: parsed.sessionId || null, code: parsed.code || null };
            }
        } catch {
            // Fall through to the transport-level message below.
        }
    }
    return { message: error?.message || 'The reply could not be sent.', outcome: null };
}

// BL-177 Step 2: an "unconfirmed" outcome means the text may still have gone out. The error carries it so the
// composer can avoid offering a quick resend.
function sendError(message, outcome = null, sessionId = null, code = null) {
    const failure = new Error(message);
    // Comms final pass: Gated Group Communications refused this send (somebody else owns the conversation). The composer keeps
    // the typed text; every screen that shows who owns what refreshes now.
    if (code) failure.code = code;
    if (code === 'conversation_owned' && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ia:conversation-access-changed'));
    }
    if (outcome === 'unconfirmed') failure.outcome = 'unconfirmed';
    // BL-177 Step 2c: a brand-new the imported provider conversation whose first text failed still exists; the caller opens it.
    if (sessionId) failure.sessionId = sessionId;
    return failure;
}

/**
 * Invokes the live staff-send-reply Edge Function. Resolves with the function's response body
 * ({ ok: true, sessionId, sent: true }); rejects with a plain Error carrying a message safe to
 * show a staff member.
 */
const composeKeys = new WeakMap();
export async function sendStaffReply({
    session,
    body = '',
    channel = null,
    subject = '',
    mediaUrl = null,
    contentSid = '',
    contentVariables = null,
    templateBody = '',
    sendingIdentityId = null,
    sendingIdentityExplicitlyChosen = false,
    campaignRecipientId = null,
    orgId = '',
    recipientAddress = '',
    groupAddresses = null,
    retryClientRef = '',
    clientRef = '',
    cc = '',
    bcc = '',
    html = '',
    client = supabaseClient,
} = {}) {
    const validationSession = session || {
        id: 'new-recipient',
        channel,
        contact_phone: channel === 'email' ? '' : (recipientAddress || (Array.isArray(groupAddresses) ? groupAddresses[0] || '' : '')),
        contact_email: channel === 'email' ? recipientAddress : '',
    };
    const validation = validateStaffReply({ session: validationSession, body, channel, contentSid, mediaUrl });
    if (!validation.valid) {
        throw new Error(validation.error);
    }
    const payload = buildStaffReplyPayload({
        session,
        body,
        channel,
        subject,
        mediaUrl,
        contentSid,
        contentVariables,
        templateBody,
        sendingIdentityId,
        sendingIdentityExplicitlyChosen,
        campaignRecipientId,
        orgId,
        recipientAddress,
        groupAddresses,
        retryClientRef,
        clientRef,
        cc,
        bcc,
        html,
    });
    // A failed transport retry reuses the same compose identity. Successful sends
    // release it so a deliberate new message with the same words is still possible.
    if(!composeKeys.has(client))composeKeys.set(client,new Map());
    const keys=composeKeys.get(client);
    const fingerprint=JSON.stringify(payload);
    if(!payload.clientRef){if(!keys.has(fingerprint))keys.set(fingerprint,newClientRef());payload.clientRef=keys.get(fingerprint);}
    const { data, error } = await client.functions.invoke(STAFF_REPLY_FUNCTION, { body: payload });
    if (error) {
        const parsed = await readFunctionError(error);
        throw sendError(parsed.message, parsed.outcome, parsed.sessionId, parsed.code);
    }
    if (data?.error) {
        throw sendError(typeof data.error === 'string' ? data.error : 'The reply could not be sent.', data.outcome, data.sessionId);
    }
    if (data && data.ok === false) {
        throw new Error('The reply could not be sent.');
    }
    // 1.12.212-beta: an email held at the Comms TEST door was not sent; say so instead of "Sent".
    if (payload.channel === 'email' && data?.caught && data.sent === false) {
        keys.delete(fingerprint);
        throw sendError(typeof data.message === 'string' && data.message ? data.message : 'The email was not sent: Communications is in Test.');
    }
    if(!data?.pending)keys.delete(fingerprint);
    return data ?? null;
}
